// frontend/src/services/inventoryService.ts
// Service layer: maps backend Resource model ↔ frontend InventoryItem model

import api from './api';
import type { Resource, Recipe, RecipeIngredient as BackendRecipeIngredient } from '../types/models';
import type { InventoryItem, RecipeIngredient } from '../components/inventaire/inventaireTypes';

// =============================================================================
// Adapters: Backend → Frontend
// =============================================================================

/** Map a backend Resource to the frontend InventoryItem shape */
export function resourceToInventoryItem(r: Resource, recipes?: Recipe[]): InventoryItem {
  // Find if this resource is used as a recipe ingredient (to detect type)
  const linkedRecipe = recipes?.find(
    rec => rec.ingredients?.some(ing => ing.resourceId === r.id)
  );

  // Determine typeItem from backend data
  let typeItem: InventoryItem['typeItem'] = 'produit_simple';
  if (linkedRecipe) {
    typeItem = 'ingredient';
  }

  // Prefer the backend _count when available (cheaper and authoritative);
  // otherwise fall back to scanning the recipes list passed to this adapter.
  const recipeUseCount = r._count?.recipeIngredients
    ?? (recipes
      ? recipes.filter(rec => rec.ingredients?.some(ing => ing.resourceId === r.id)).length
      : undefined);
  const supplierCount = r._count?.supplierResources;

  const recipeNames: string[] | undefined = Array.isArray(r.recipeIngredients)
    ? r.recipeIngredients.map((ri: any) => ri.recipe?.name).filter(Boolean)
    : undefined;

  const supplierDetails: { name: string; unitCost: number | null; isPreferred: boolean }[] | undefined =
    Array.isArray(r.supplierResources)
      ? r.supplierResources.map((sr: any) => ({
          name: sr.supplier?.name ?? '?',
          unitCost: sr.unitCost != null ? Number(sr.unitCost) : null,
          isPreferred: sr.isPreferred === true,
        }))
      : undefined;

  return {
    id: r.id,
    nom: r.name,
    categorie: r.category || 'Non catégorisé',
    image: '',
    prixVente: Number(r.sellPrice) || 0,
    prixCoutant: Number(r.costPrice) || 0,
    quantite: Number(r.currentQuantity) || 0,
    statut: r.isActive ? 'Actif' : 'Inactif',
    source: r.veloceResourceId ? 'pos' : 'manuel',
    typeItem,
    formatQuantite: r.containerCapacity ?? undefined,
    formatUnite: r.containerUnit ?? undefined,
    seuilStock: r.minQuantity ?? undefined,
    recipeUseCount,
    supplierCount,
    recipeNames,
    supplierDetails,
  };
}

/** Map a backend Recipe to the frontend InventoryItem shape (for the recettes tab) */
export function recipeToInventoryItem(rec: Recipe): InventoryItem {
  const composition: RecipeIngredient[] = (rec.ingredients || []).map((ing: BackendRecipeIngredient) => ({
    itemId: ing.resourceId,
    nom: ing.resource?.name || ing.resourceId,
    quantite: ing.quantity,
    unite: ing.unit,
    prixCoutant: ing.resource?.costPrice != null ? Number(ing.resource.costPrice) : undefined,
  }));

  return {
    id: rec.id,
    nom: rec.name,
    categorie: rec.category || 'Non catégorisé',
    image: '',
    prixVente: Number(rec.sellPrice) || 0,
    prixCoutant: Number(rec.calculatedCost) || 0,
    quantite: 0, // Recipes don't have stock quantity
    statut: rec.isActive ? 'Actif' : 'Inactif',
    source: rec.veloceItemId ? 'pos' : 'manuel',
    typeItem: 'recette',
    recetteId: rec.id,
    composition,
  };
}

// =============================================================================
// Adapters: Frontend → Backend
// =============================================================================

/** Map frontend InventoryItem fields to a backend Resource update payload */
export function inventoryItemToResourceUpdate(item: Partial<InventoryItem>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (item.nom !== undefined) payload.name = item.nom;
  if (item.categorie !== undefined) payload.category = item.categorie;
  if (item.prixVente !== undefined) payload.sellPrice = item.prixVente;
  if (item.prixCoutant !== undefined) payload.costPrice = item.prixCoutant;
  if (item.quantite !== undefined) payload.currentQuantity = item.quantite;
  if (item.statut !== undefined) payload.isActive = item.statut === 'Actif';
  if (item.seuilStock !== undefined) payload.minQuantity = item.seuilStock;
  if (item.formatQuantite !== undefined) payload.containerCapacity = item.formatQuantite;
  if (item.formatUnite !== undefined) payload.containerUnit = item.formatUnite;
  return payload;
}

// =============================================================================
// API Calls
// =============================================================================

export async function fetchResources(params?: {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  lowStock?: boolean;
}): Promise<{ resources: Resource[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const response = await api.get('/resources', { params: { limit: 500, ...params } });
  return response.data.data;
}

export async function fetchRecipes(params?: {
  category?: string;
  search?: string;
}): Promise<{ recipes: Recipe[] }> {
  const response = await api.get('/recipes', { params: { limit: 500, ...params } });
  return response.data.data;
}

export async function updateResource(id: string, data: Record<string, unknown>): Promise<Resource> {
  const response = await api.put(`/resources/${id}`, data);
  return response.data.data;
}

export async function createResource(data: Record<string, unknown>): Promise<Resource> {
  const response = await api.post('/resources', data);
  return response.data.data;
}

export async function adjustResourceQuantity(
  id: string,
  quantity: number,
  movementType: string,
  notes?: string
): Promise<void> {
  await api.post(`/resources/${id}/adjust`, { quantity, movementType, notes });
}
